import React from "react";
import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Title from "./title";
import avatar from "../../images/1200px-ChatGPT_logo.svg.png";
import { apiURL } from "../../config";
import { useSelector, useDispatch } from "react-redux";
import { setUser, clearUser } from "../../redux/userSlice";
import { newConversation } from "../../redux/conversationIdSlice";
import { setConversations } from "../../redux/conversationsSlice";
import { setSideBar } from "../../redux/sideBarSlice";
import { setMessagesStatus } from "../../redux/messagesStatusSlice";

function SideBar() {
  //console.log('render sidebar')
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const historyRef = useRef(null);
  const conversations = useSelector((state) => state.conversations);
  const sideBarState = useSelector((state) => state.sideBar);
  const [showStarredOnly, setShowStarredOnly] = useState(false);

  useEffect(() => {
    //console.log('sidebar effect')
    let ignore = false;
    const fetchConversations = async () => {
      try {
        //console.log(`fetch ${apiURL}/api`)
        const res = await fetch(`${apiURL}/api`, { credentials: "include" });
        const data = await res.json();
        //console.log(data)
        if (!ignore) {
          if (data.user.username === "") {
            const localConversations = JSON.parse(
              localStorage.getItem("conversations")
            );
            if (localConversations) {
              dispatch(setConversations(localConversations));
            } else {
              dispatch(setConversations([]));
            }
            dispatch(clearUser());
          } else {
            dispatch(setConversations([...data.conversations]));
            dispatch(
              setUser({ _id: data.user._id, username: data.user.username })
            );
          }
        } else {
          console.log("ignore");
        }
      } catch (error) {
        console.log("Error fetching conversations: ", error);
      }
    };

    fetchConversations();
    return () => {
      ignore = true;
    };
  }, [dispatch]);

  const handleOverlayClick = () => {
    dispatch(setSideBar("hidden"));
  };

  return (
    <>
      <div
        id="sidebar"
        className={`flex flex-col shrink-0 p-2 text-white bg-[hsla(var(--bg-000))] w-52 top-0 z-[910] h-full transition-all ${
          sideBarState === "left-sidebar" && "relative left-0"
        } ${sideBarState === "hidden" && "absolute -left-52"} ${
          sideBarState === "float" && "absolute left-0"
        }`}
      >
        <span
          className="group flex justify-center items-center rounded-xl py-3 mb-2 border border-[hsla(50_5.8%_19%)] shadow-sm shadow-neutral-800 bg-gradient-to-b from-[hsla(60_1.8%_32%/0.7)] from-0% to-[hsla(var(--bg-300)/0.3)] to-[90%]  cursor-pointer text-white relative before:absolute before:inset-0 before:rounded-xl before:z-[0] before:bg-[hsla(var(--bg-000)/0.6)]"
          id="newChat"
          onClick={() => {
            dispatch(newConversation());
            dispatch(setMessagesStatus("NEW"));
          }}
        >
          <span className="relative text-xl font-bold -translate-x-1  group-hover:scale-110 transition-transform duration-200">
            <div className="inline-block w-6 h-6 translate-y-1 mx-1">
              {rightArrowSVG}
            </div>{" "}
            New Chat
          </span>
        </span>

        <hr className="mb-0" />

        <div className="flex select-none">
          <div
            className="flex flex-row py-2 pr-4 justify-start items-start cursor-pointer group"
            onClick={() => setShowStarredOnly(!showStarredOnly)}
          >
            <div className="text-sm text-gray-100 w-4 h-4">{filterSVG}</div>
            <span
              className={`text-xs pl-1 ${
                showStarredOnly ? "text-yellow-400" : "text-gray-400"
              } group-hover:text-yellow-400/50 transition-colors`}
            >
              ★
            </span>
          </div>
        </div>

        <ul
          id="historyUl"
          ref={historyRef}
          onMouseEnter={() => {
            historyRef.current.classList.remove("scrolling");
          }}
          onMouseLeave={() => {
            historyRef.current.classList.add("scrolling");
          }}
          className="flex-col h-full mb-auto pb-[100px] px-1 scrolling overflow-x-hidden overflow-y-auto"
        >
          {conversations
            .filter((conv) => !showStarredOnly || conv.star)
            .map((conv) => {
              return (
                <Title
                  key={conv._id}
                  cid={conv._id}
                  title={conv.title}
                  star={conv.star ?? false}
                />
              );
            })}
        </ul>
        <hr className="mb-2" />
        {user.username === "" ? (
          <Link
            to="/login"
            className="mx-auto text-white px-16 py-1 hover:scale-110 hover:bg-gray-800 transition rounded-lg"
          >
            <strong>Log in</strong>
          </Link>
        ) : (
          <UserDropDown user={user} />
        )}
      </div>
      <div
        onClick={handleOverlayClick}
        className={`fixed top-0 left-0 bottom-0 right-0 bg-black bg-opacity-50 z-[900] transition-opacity duration-500 ${
          sideBarState === "float"
            ? "opacity-100"
            : "opacity-0 pointer-events-none"
        }`}
      ></div>
    </>
  );
}

const UserDropDown = function ({ user }) {
  const navigate = useNavigate();
  const dropDownRef = useRef(null);
  const dropDownContainerRef = useRef(null);
  const dispatch = useDispatch();

  const handleClickDropDown = () => {
    const dropDown = dropDownRef.current;
    if (dropDown.style.gridTemplateRows === "0fr") {
      dropDown.style.gridTemplateRows = "1fr";
    } else {
      dropDown.style.gridTemplateRows = "0fr";
    }
  };

  const handleClickOutside = (event) => {
    if (
      dropDownContainerRef.current &&
      !dropDownContainerRef.current.contains(event.target)
    ) {
      dropDownRef.current.style.gridTemplateRows = "0fr";
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleUsage = function (event) {
    event.preventDefault();
    navigate("/usage");
  };

  const handleLogOut = async function (event) {
    event.preventDefault();
    const response = await fetch(`${apiURL}/api/logout`, {
      method: "GET",
      credentials: "include",
    });

    const data = await response.json();
    console.log(data);
    if (data.message === "SUCCESS") {
      // setUser({ _id: "", username: "" });
      dispatch(clearUser());
      window.location.reload();
    }
  };

  const handleExport = async function (event) {
    event.preventDefault();
    try {
      const response = await fetch(`${apiURL}/api/conversation/export`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      } else {
        const data = await response.json();
        //console.log(data);
        const blob = new Blob([JSON.stringify(data)], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.download = "chats.json";
        a.href = url;
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log("export error", error);
    }
  };

  const handleUpload = async function (event) {
    event.preventDefault();
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".json";
    fileInput.click();
    fileInput.onchange = async function (event) {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append("file", file);
      try {
        const response = await fetch(`${apiURL}/api/conversation/import`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
        if (response.ok) {
          const data = await response.json();
          console.log(data);
          window.location.reload();
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.log("upload error", error);
        alert("Upload failed");
      }
    };
  };

  return (
    <div className="flex flex-row  items-center text-white select-none">
      <img
        src={avatar}
        className="w-8 h-8 rounded-full border border-white "
        alt=""
      />
      <div
        className="group grow relative h-full mx-2 flex justify-center items-center rounded hover:bg-gray-800 cursor-pointer"
        onClick={handleClickDropDown}
        ref={dropDownContainerRef}
      >
        <span className="px-2 font-bold group-hover:scale-110 group-hover:-translate-x-0.5 transition ">
          {user.username}
        </span>
        <span className="scale-75 group-hover:translate-x-0.5 transition">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="white"
          >
            <path d="M24 22h-24l12-20z" />
          </svg>
        </span>
        <DropDownMenu rf={dropDownRef}>
          <DropDownItem onClick={handleUsage}>Usage</DropDownItem>
          <DropDownItem onClick={handleExport}>Export History</DropDownItem>
          <DropDownItem onClick={handleUpload}>Recover History</DropDownItem>
          <hr className="mt-2 mb-1" />
          <DropDownItem onClick={handleLogOut}>Sign out</DropDownItem>
        </DropDownMenu>
      </div>
    </div>
  );
};

function DropDownMenu({ rf, children }) {
  return (
    <span
      className="absolute grid right-0 bottom-[140%] bg-gray-700 z-10 rounded-md"
      style={{
        gridTemplateRows: "0fr",
        transition: "grid-template-rows 0.1s ease-out",
      }}
      ref={rf}
    >
      <span className="overflow-hidden">{children}</span>
    </span>
  );
}

function DropDownItem({ onClick, children }) {
  return (
    <div
      className="block text-white text-md font-sans font-medium tracking-wide antialiased w-full px-3 py-1 first:mt-3 last:mb-2 cursor-pointer hover:bg-gray-600 text-nowrap"
      onClick={onClick}
    >
      {children}
    </div>
  );
}

const rightArrowSVG = (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="none"
    color="white"
    className="translate-x-[1px] -translate-y-[1px]"
  >
    <g id="SVGRepo_iconCarrier">
      {" "}
      <path d="M19 14V22H2.99997V4H13"></path>{" "}
      <path d="M17.4608 4.03921C18.2418 3.25817 19.5082 3.25816 20.2892 4.03921L20.9608 4.71079C21.7418 5.49184 21.7418 6.75817 20.9608 7.53921L11.5858 16.9142C11.2107 17.2893 10.702 17.5 10.1716 17.5L7.5 17.5L7.5 14.8284C7.5 14.298 7.71071 13.7893 8.08579 13.4142L17.4608 4.03921Z"></path>{" "}
      <path d="M16.25 5.25L19.75 8.75"></path>{" "}
    </g>
  </svg>
);

const filterSVG = (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
    <g
      id="SVGRepo_tracerCarrier"
      stroke-linecap="round"
      stroke-linejoin="round"
    ></g>
    <g id="SVGRepo_iconCarrier">
      {" "}
      <path
        d="M21 6H19M21 12H16M21 18H16M7 20V13.5612C7 13.3532 7 13.2492 6.97958 13.1497C6.96147 13.0615 6.93151 12.9761 6.89052 12.8958C6.84431 12.8054 6.77934 12.7242 6.64939 12.5617L3.35061 8.43826C3.22066 8.27583 3.15569 8.19461 3.10948 8.10417C3.06849 8.02393 3.03853 7.93852 3.02042 7.85026C3 7.75078 3 7.64677 3 7.43875V5.6C3 5.03995 3 4.75992 3.10899 4.54601C3.20487 4.35785 3.35785 4.20487 3.54601 4.10899C3.75992 4 4.03995 4 4.6 4H13.4C13.9601 4 14.2401 4 14.454 4.10899C14.6422 4.20487 14.7951 4.35785 14.891 4.54601C15 4.75992 15 5.03995 15 5.6V7.43875C15 7.64677 15 7.75078 14.9796 7.85026C14.9615 7.93852 14.9315 8.02393 14.8905 8.10417C14.8443 8.19461 14.7793 8.27583 14.6494 8.43826L11.3506 12.5617C11.2207 12.7242 11.1557 12.8054 11.1095 12.8958C11.0685 12.9761 11.0385 13.0615 11.0204 13.1497C11 13.2492 11 13.3532 11 13.5612V17L7 20Z"
        stroke="#d0d0d0"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      ></path>{" "}
    </g>
  </svg>
);

export default SideBar;
